import { Feature, User } from "@/types/auth";
import { UserRole } from "@/types/user";
import { baseUrl, queryClient } from "@/utils/api";
import { storage } from "@/utils/storage";
import { queryOptions, useMutation } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { atomWithQuery } from "jotai-tanstack-query";
import ky from "ky";
import { useMemo } from "react";

// export type Claims = {
//   authType?: string | null;
//   multipleOrganizations?: boolean | null;
//   userBrowserId?: null | null;
//   readOnly?: boolean | null;
//   portalAccess?: boolean | null;
//   solutionVersion?: string | null;
//   lassoSalesAccess?: boolean | null;
//   printerUser?: boolean | null;
//   ipAccess?: boolean | null;
//   impersonating?: boolean | null;
//   uak?: boolean | null;
//   whitelabel?: boolean | null;
// };

export type LoginResponse = {
  user: User;
  token: string;
  features: Feature[];
  claims: { lassoSalesAccess?: boolean; [key: string]: unknown }; // Claims;
};

export const subscriptionSolutions = [
  "lso_sales_standard",
  "lso_sales_pro",
] as const;

export const subscriptionSolutionsSet = new Set<
  (typeof subscriptionSolutions)[number] | (string & {})
>(subscriptionSolutions);

export const subscriptionFeatures = [
  Feature.LassoSalesFreemium,
  Feature.LassoSalesStandard,
  Feature.LassoSalesPro,
] as const;

export const subscriptionFeaturesSet = new Set<
  (typeof subscriptionFeatures)[number] | (string & {})
>(subscriptionFeatures);

/**
 * Thrown when a session that was established with a login token (`?lt=`) needs to log in again.
 * Impersonation tokens are session-scoped and write no refresh cookie, so falling back to the
 * cookie would silently sign us in as the impersonator instead.
 */
export class ImpersonationSessionExpiredError extends Error {
  constructor() {
    super("The impersonated session has expired.");
    this.name = "ImpersonationSessionExpiredError";
  }
}

let isImpersonatedSession = false;

const authFn = async () => {
  // Only sent on the first login - the token is short-lived and single-use in practice.
  const loginToken = storage.getLoginToken();

  if (!loginToken && isImpersonatedSession) {
    throw new ImpersonationSessionExpiredError();
  }

  const response = await ky
    .post(baseUrl + "/login", {
      cache: "no-store",
      credentials: "include",
      timeout: false,
      // Don't delete the empty object, endpoint expects a body. When a login token is present the
      // API uses it instead of the refresh cookie.
      json: loginToken ? { loginToken } : {},
    })
    .json<LoginResponse>()
    .then((auth) => {
      const featuresSet = new Set(auth.features);

      const isAuthorized = !!auth.claims.lassoSalesAccess;

      return {
        ...auth,
        featuresSet,
        isAuthorized,
      };
    })
    .catch((error) => {
      throw error;
    });

  if (loginToken) {
    storage.clearLoginToken();
    isImpersonatedSession = !!response.claims.impersonating;
  }

  storage.setToken(response.token);
  return response;
};

export type Auth = Awaited<ReturnType<typeof authFn>>;

export const authQueryOptions = queryOptions({
  queryKey: ["auth"],
  queryFn: authFn,
  gcTime: Infinity,
  staleTime: 4 * 60 * 60 * 1000, // 4 hours, same as token expiry
  retry: false,
});

export const authAtom = atomWithQuery(
  () => authQueryOptions,
  () => queryClient,
);

// Full auth state hook for router and initialization code
export function useAuthState() {
  return useAtomValue(authAtom);
}

export function useAuth() {
  const { data, isPending, isError, error } = useAuthState();

  if (isPending) {
    throw new Error(
      "Auth is still loading. This should not happen in a protected route.",
    );
  }

  if (isError || !data) {
    throw error;
  }

  return data;
}

export function useLogout() {
  return useMutation({
    mutationFn: () =>
      ky.get(baseUrl + "/logout", {
        cache: "no-store",
        credentials: "include",
      }),
    onSuccess: () => {
      queryClient.setQueryData(["auth"], null);
      storage.clearToken();
    },
  });
}

export function hasRole(user: User, role: UserRole) {
  return user.roles.includes(role);
}

export function isAdmin(user: User) {
  return (
    user.roles.includes(UserRole.Admin) ||
    user.roles.includes(UserRole.LassoAdmin) ||
    user.roles.includes(UserRole.Salesleader)
  );
}

export function isLassoAdmin(user: User) {
  return user.roles.includes(UserRole.LassoAdmin);
}

export function hasInsightsAccess(user: User) {
  return (
    user.roles.includes(UserRole.User) ||
    user.roles.includes(UserRole.Admin) ||
    user.roles.includes(UserRole.LassoAdmin)
  );
}

// Roles from lowest to highest level.
export const userRoleHierarchy = new Map(
  [
    UserRole.Sales,
    UserRole.Salesleader,
    UserRole.Admin,
    UserRole.LassoAdmin,
  ].map((role, index) => [role, index] as const),
);

export function getUserRoleLevel(user: User) {
  let highestLevel = -1;
  for (const role of user.roles) {
    const level = userRoleHierarchy.get(role) ?? -1;
    if (level > highestLevel) {
      highestLevel = level;
    }
  }
  return highestLevel;
}

export function useIsAdmin() {
  const { user } = useAuth();
  return useMemo(() => (user ? isAdmin(user) : false), [user]);
}

export const getUserFromCache = () => {
  return queryClient.getQueryData<LoginResponse>(["auth"]);
};

export const solutionVersionPlanMap = new Map([
  ["lso_sales_essentials", "essentials" as const],
  ["lso_sales_enterprise", "enterprise" as const],
  ["lso_sales_pro", "pro" as const],
]);
